import { useEffect, useRef } from 'react';

interface ShortcutActions {
  onToggleScanner?: () => void;
  onOpenPayment?: () => void;
  onCustomerLookup?: () => void;
  onParkBill?: () => void;
  onResumeParked?: () => void;
  onCloseModal?: () => void;
  onFocusSearch?: () => void;
  onApplyBillDiscount?: () => void;
  onCompletePayment?: () => void;
  onVoidBill?: () => void;
  onUndoLastItem?: () => void;
  /** `?` (Shift+/) — show the keyboard shortcut help overlay. */
  onShowHelp?: () => void;
}

/**
 * POS Billing keyboard shortcuts (PAGES.md §2.2):
 *   F1  → Focus search bar
 *   F2  → Toggle barcode scanner (also fires while an input is focused)
 *   F3  → Open customer lookup
 *   F4  → Park current bill
 *   F5  → Resume parked bills
 *   F8  → Apply bill-level discount
 *   F9  → Open payment modal (also fires while an input is focused)
 *   F10 → Complete sale (only wired when payment modal is open; fires even
 *         when the amount-paid input is focused)
 *   F12 → Void current draft bill (with confirmation)
 *   Esc → Close any open modal/drawer
 *   /   → Focus search (when not in an input)
 *   ?   → Show shortcut help overlay (only outside inputs; Shift+/ on
 *         US keyboards — we listen for `key === '?'` so other layouts
 *         that type `?` with different modifiers still work)
 *   Ctrl+Z → Remove last added cart item (ONLY when not in an input —
 *         inside inputs the browser's native text-undo wins)
 *
 * Implementation note: actions are stored in a ref that's updated every
 * render, while the keydown listener is attached **once** for the lifetime
 * of the consumer. This prevents listener churn when the parent passes
 * inline arrow callbacks (which is the common case in BillingPage).
 */
export function useKeyboardShortcuts(actions: ShortcutActions) {
  const actionsRef = useRef(actions);
  // Refresh on every render so the listener always reads the freshest
  // closures (latest `items`, modal state, etc.).
  actionsRef.current = actions;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const a = actionsRef.current;
      // Skip if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      const inEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (inEditable) {
        // Inside editable elements, only Escape + a small allowlist of
        // F-keys are honoured. Everything else (F8, F12, Ctrl+Z, /) must
        // fall through to the browser / input so cashiers can type.
        if (e.key === 'Escape') {
          a.onCloseModal?.();
          (target as HTMLInputElement).blur();
        }
        if (e.key === 'F2') {
          e.preventDefault();
          a.onToggleScanner?.();
        }
        if (e.key === 'F9') {
          e.preventDefault();
          a.onOpenPayment?.();
        }
        // F10 must still fire when the amount-paid input is focused inside
        // the payment modal — that's the whole point of the shortcut.
        if (e.key === 'F10') {
          e.preventDefault();
          a.onCompletePayment?.();
        }
        return;
      }

      // Ctrl+Z (inverse rule): only fire outside inputs so the browser's
      // native text-undo still works inside the amount-paid field.
      if (e.ctrlKey && !e.metaKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        a.onUndoLastItem?.();
        return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          a.onFocusSearch?.();
          break;
        case 'F2':
          e.preventDefault();
          a.onToggleScanner?.();
          break;
        case 'F3':
          e.preventDefault();
          a.onCustomerLookup?.();
          break;
        case 'F4':
          e.preventDefault();
          a.onParkBill?.();
          break;
        case 'F5':
          e.preventDefault();
          a.onResumeParked?.();
          break;
        case 'F8':
          // Chrome/Edge bind F8 to "pause script execution" in devtools —
          // preventDefault so nothing escapes into the debugger.
          e.preventDefault();
          a.onApplyBillDiscount?.();
          break;
        case 'F9':
          e.preventDefault();
          a.onOpenPayment?.();
          break;
        case 'F10':
          e.preventDefault();
          a.onCompletePayment?.();
          break;
        case 'F12':
          // Chrome binds F12 to devtools — preventDefault here keeps the
          // shortcut inside the POS terminal.
          e.preventDefault();
          a.onVoidBill?.();
          break;
        case 'Escape':
          a.onCloseModal?.();
          break;
        case '/':
          e.preventDefault();
          a.onFocusSearch?.();
          break;
        case '?':
          // Shift+/ on most layouts. Only fires outside editable targets
          // (guarded above) so a `?` typed into the amount-paid field
          // still reaches the input.
          e.preventDefault();
          a.onShowHelp?.();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Empty deps — listener attaches once. The actionsRef is refreshed by
    // every render so the closure always sees the latest callbacks.
  }, []);
}
